import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { forwardRef } from 'react';
import { cn } from './cn.js';
// Token-backed utilities only — no hardcoded colors (D-02).
const VARIANT_CLASSES = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    outline: 'border border-border bg-transparent text-foreground hover:bg-secondary',
    ghost: 'bg-transparent text-foreground hover:bg-accent hover:text-accent-foreground ' +
        'disabled:bg-transparent disabled:outline-none',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
};
const SIZE_CLASSES = {
    // 44px minimum interactive target (A11Y-01).
    sm: 'h-11 gap-1.5 px-3 text-sm',
    default: 'h-11 gap-2 px-4 py-2 text-sm',
    lg: 'h-12 gap-2 px-6 text-base',
    icon: 'size-11',
};
const BASE_CLASSES = 'inline-flex items-center justify-center whitespace-nowrap rounded-md font-semibold ' +
    // Press feedback + color transitions on the snappy token curve; respect reduced motion.
    'transition-[transform,background-color,color,box-shadow] duration-[var(--dur-fast)] ' +
    'ease-[var(--ease-out)] active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100 ' +
    // Visible keyboard focus ring driven by the --ring token (A11Y-01).
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
    // Disabled is a DESIGNED state, not a dimmer. `opacity-50` faded the label
    // and the fill together and landed wherever the arithmetic landed — 2.07:1
    // for the label on the orange primary, 1.78:1 for the fill against the page.
    // These name it: the --disabled surface, the --disabled-foreground label
    // (>=5.8:1 on that surface in both themes), and the same ink as a 1px inset
    // outline so the control still has a >=6.9:1 boundary. An outline rather than
    // a border because it costs no layout — an enabled button is unchanged, and
    // nothing shifts by 2px when the button becomes available.
    'disabled:pointer-events-none disabled:bg-disabled disabled:text-disabled-foreground ' +
    'disabled:outline-1 disabled:-outline-offset-1 disabled:outline-disabled-foreground ' +
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0';
/**
 * The Button's own class string, for the rare control that cannot BE a Button but
 * must look like one — a trigger some other primitive renders and owns the ref of.
 * Exported so such a control reads the real definition instead of copying six
 * utilities that then drift from it.
 *
 * ⚠ It carries the ENABLED look only. Disabled and loading are behaviours a real
 * `<button>` provides; a caller borrowing the classes is responsible for its own.
 */
export function buttonClasses(options) {
    const { variant = 'primary', size = 'default', className } = options ?? {};
    return cn(BASE_CLASSES, VARIANT_CLASSES[variant], SIZE_CLASSES[size], className);
}
/**
 * Token-driven, WCAG-2.1-AA Button primitive.
 *
 * Renders a real <button> (semantic HTML) with a visible focus-visible ring.
 * Its label comes from children (a t() slot in app code) — never a hardcoded
 * display string in the primitive. `loading` shows a spinner and disables the
 * control while keeping the label so width doesn't jump. When `asChild` is set,
 * icon/loading affordances are ignored (the child owns its own content).
 */
export const Button = forwardRef(({ className, variant = 'primary', size = 'default', asChild = false, loading = false, iconLeft, iconRight, type, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const classes = buttonClasses({ variant, size, className });
    if (asChild) {
        return (_jsx(Comp, { ref: ref, className: classes, ...props, children: children }));
    }
    return (_jsxs(Comp, { ref: ref, type: type ?? 'button', className: classes, disabled: disabled || loading, "aria-busy": loading || undefined, ...props, children: [loading ? (_jsx(Loader2, { className: "animate-spin motion-reduce:animate-none", "aria-hidden": "true" })) : (iconLeft && (_jsx("span", { className: "contents", "aria-hidden": "true", children: iconLeft }))), children, !loading && iconRight && (_jsx("span", { className: "contents", "aria-hidden": "true", children: iconRight }))] }));
});
Button.displayName = 'Button';
